import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, User, CheckCircle, AlertCircle, Timer } from 'lucide-react';

const BookInspectionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inspectionId = searchParams.get('id');
  
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Generate time slots from 9 AM to 6 PM (30-minute intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: time, display: displayTime });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Format time remaining
  const formatTimeRemaining = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return null;
    
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  useEffect(() => {
    if (!inspectionId) {
      setError('No inspection ID provided');
      setLoading(false);
      return;
    }

    const fetchInspectionDetails = async () => {
      try {
        const response = await fetch(`/api/inspection-booking/details/${inspectionId}`);
        const data = await response.json();

        if (data.success) {
          setInspection(data.inspection);
          
          // Check expiration
          if (data.inspection.isExpired) {
            setIsExpired(true);
            setError('This booking form has expired (72 hours). Please contact support for assistance.');
          } else if (data.inspection.timeRemaining) {
            setTimeRemaining(data.inspection.timeRemaining);
            
            // Set up countdown timer
            const timer = setInterval(() => {
              const now = new Date().getTime();
              const expiresAt = new Date(data.inspection.emailExpiresAt).getTime();
              const remaining = expiresAt - now;
              
              if (remaining <= 0) {
                setIsExpired(true);
                setError('This booking form has expired (72 hours). Please contact support for assistance.');
                clearInterval(timer);
              } else {
                setTimeRemaining(remaining);
              }
            }, 60000); // Update every minute

            return () => clearInterval(timer);
          }
        } else {
          setError(data.message || 'Failed to load inspection details');
        }
      } catch (error) {
        console.error('Error fetching inspection details:', error);
        if (error.message === '410') {
          setIsExpired(true);
          setError('This booking form has expired (72 hours). Please contact support for assistance.');
        } else {
          setError('Failed to load inspection details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInspectionDetails();
  }, [inspectionId]);

  const handleSlotChange = (date, timeSlot) => {
    setSelectedSlots(prev => ({
      ...prev,
      [date]: timeSlot
    }));
  };

  const handleSubmit = async () => {
    // Check if form has expired
    if (isExpired) {
      alert('This booking form has expired. Please contact support for assistance.');
      return;
    }

    // Validate that at least one time slot is selected
    const selectedDates = Object.keys(selectedSlots).filter(date => selectedSlots[date]);
    
    if (selectedDates.length === 0) {
      alert('Please select at least one time slot');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/inspection-booking/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inspectionId: inspectionId,
          selectedSlots: selectedSlots
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        if (data.expired) {
          setIsExpired(true);
          setError('This booking form has expired (72 hours). Please contact support for assistance.');
        } else {
          alert(data.message || 'Failed to submit time slot selection');
        }
      }
    } catch (error) {
      console.error('Error submitting time slots:', error);
      alert('Failed to submit time slot selection');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inspection details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Booking Confirmed!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for selecting your preferred time slots. We'll contact you shortly to confirm the final inspection time.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="text-center mb-8">
            <Calendar className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Schedule Your Property Inspection
            </h1>
            <p className="text-gray-600 mb-4">
              Please select your preferred time slots for the available dates
            </p>
            
            {/* Expiration Notice */}
            {timeRemaining && !isExpired && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
                <div className="flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                  <p className="text-yellow-800 font-medium">
                    {formatTimeRemaining(timeRemaining)}
                  </p>
                </div>
                <p className="text-yellow-700 text-sm mt-1">
                  This form expires 72 hours after the email was sent
                </p>
              </div>
            )}
            
            {isExpired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md mx-auto">
                <div className="flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <p className="text-red-800 font-medium">
                    Booking form has expired
                  </p>
                </div>
                <p className="text-red-700 text-sm mt-1">
                  Please contact support for assistance
                </p>
              </div>
            )}
          </div>

          {/* Inspection Details */}
          {inspection && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Inspection Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Customer</p>
                    <p className="font-medium">{inspection.customerName || 'Anas Test'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Property</p>
                    <p className="font-medium">{inspection.propertyAddress || '123 Test Street, Singapore 220001'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Time Slot Selection */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Available Dates & Time Slots</h2>
          
          {isExpired ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Booking Form Expired</h3>
              <p className="text-gray-600 mb-6">
                This booking form expired after 72 hours. Please contact our support team to reschedule your inspection.
              </p>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Support Email: support@swiftfixpro.com</p>
                <p className="text-sm text-gray-500">Support Phone: +65 1234 5678</p>
              </div>
            </div>
          ) : (
            <>
              {/* Demo dates if no inspection data */}
              {(!inspection?.availableDates || inspection.availableDates.length === 0) && (
            <div className="space-y-6">
              {['2024-12-20', '2024-12-21', '2024-12-22'].map((date) => (
                <div key={date} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {timeSlots.map((slot) => (
                      <label
                        key={`${date}-${slot.value}`}
                        className={`
                          flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all
                          ${selectedSlots[date] === slot.value
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          name={`time-${date}`}
                          value={slot.value}
                          checked={selectedSlots[date] === slot.value}
                          onChange={(e) => handleSlotChange(date, e.target.value)}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <Clock className="w-4 h-4 mx-auto mb-1" />
                          <span className="text-sm font-medium">{slot.display}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {selectedSlots[date] && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm">
                        ✓ Selected: {timeSlots.find(s => s.value === selectedSlots[date])?.display}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Real inspection dates if available */}
          {inspection?.availableDates && inspection.availableDates.length > 0 && (
            <div className="space-y-6">
              {inspection.availableDates.map((date) => (
                <div key={date} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {timeSlots.map((slot) => (
                      <label
                        key={`${date}-${slot.value}`}
                        className={`
                          flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all
                          ${selectedSlots[date] === slot.value
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          name={`time-${date}`}
                          value={slot.value}
                          checked={selectedSlots[date] === slot.value}
                          onChange={(e) => handleSlotChange(date, e.target.value)}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <Clock className="w-4 h-4 mx-auto mb-1" />
                          <span className="text-sm font-medium">{slot.display}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {selectedSlots[date] && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm">
                        ✓ Selected: {timeSlots.find(s => s.value === selectedSlots[date])?.display}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(selectedSlots).filter(date => selectedSlots[date]).length === 0}
              className={`
                px-8 py-3 rounded-lg font-semibold text-white transition-all
                ${submitting || Object.keys(selectedSlots).filter(date => selectedSlots[date]).length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 hover:bg-orange-600'
                }
              `}
            >
              {submitting ? 'Submitting...' : 'Confirm Time Slots'}
            </button>
            
            <p className="text-gray-500 text-sm mt-2">
              Select at least one preferred time slot to continue
            </p>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookInspectionPage;